/**
 * The `mucka_app` cookie, in a module with no imports so the native bootstrap
 * (browser) and the server guards can share one spelling of it. It must never
 * import `next/server`: `native-guard.ts` does, and a client bundle cannot.
 */

/**
 * Cookie the native bootstrap writes on every launch: `ios` / `android`, with
 * `.iap` appended when the binary ships the purchase plugin.
 *
 * 🚨 It exists because the UA token is NOT on every request, whatever the
 * comment on `NATIVE_UA_TOKEN` hoped. Android WebView sends a service worker's
 * fetches with the DEFAULT user agent — `appendUserAgent` is a WebSettings
 * override and the service worker has its own settings, which carry none — and
 * `sw.js` answers every navigation with `fetch(request)`. So once the worker
 * controls the page, a full navigation from inside the Android app reaches the
 * server looking exactly like Chrome.
 *
 * That is how "Subscribe" on the Android Subscription screen did nothing
 * (one owner, 25 Sep 2026): it went to `/paywall`, the paywall read
 * "not native" and redirected to the billing tab it had just come from, and the
 * owner saw the screen flash. 0 Google purchases on production against 4 Apple,
 * because iOS has no such worker in the way. A cookie rides a service-worker
 * fetch where the UA override does not.
 *
 * Spoofable, as the UA is — setting it only hides purchase surfaces from the
 * person who set it.
 */
export const NATIVE_APP_COOKIE = 'mucka_app' as const;

export interface NativeAppCookie {
  platform: 'ios' | 'android';
  iap: boolean;
}

/** Parse the `mucka_app` cookie value. Anything unrecognised is null (web). */
export function parseNativeAppCookie(value: string | null | undefined): NativeAppCookie | null {
  if (!value) return null;
  const [p, flag] = value.trim().toLowerCase().split('.');
  if (p !== 'ios' && p !== 'android') return null;
  return { platform: p, iap: flag === 'iap' };
}

/** The value the bootstrap writes — the one place the format is spelled. */
export function nativeAppCookieValue(platform: 'ios' | 'android', iap: boolean): string {
  return iap ? `${platform}.iap` : platform;
}
